import { ENTITY_ICONS } from "@/services/entityIcons"
import { PROJECT_FORM_NAMES } from "@/store/project"
import { packetListRoute } from "@/services/catalogPackets"
import { PERMISSIONS } from "@/services/permission"

// How each record kind the API can name is presented: its icon, what it is called, and where it
// opens. Keyed by the backend's `EntityType`, which the SPA already uses as its own type id in
// `store/recentEntities.js`, `entityTabItems()` and the global search.
//
// One place, because three features had grown their own copy of it — the command palette
// (`SEARCH_TYPES`), the file browser (`FILE_TYPES`) and now the Protokoll tab's reference list — and
// an entity whose icon or route is defined three times is an entity whose icon or route disagrees
// with itself. The two older maps build their entries from here and keep only what is genuinely
// theirs (`path` and `editPermissions` for the file browser).
//
// `routeOf(id, parentId)` takes a second argument because a Bautagesbericht, a Stundenlohnbericht and
// a Regierechnung have no route of their own: they open as a tab of the project they belong to, and
// the caller supplies that project's id. Without one there is nowhere to go, so the route is null and
// the row renders unlinked rather than linking somewhere wrong.

/** Where a report opens: as a tab of its project, since neither report type has a route of its own. */
const reportRoute = tab => (id, parentId) =>
  parentId ? { name: "ProjectDetail", params: { projectId: parentId }, hash: `#${tab}` } : null

export const ENTITY_TYPES = {
  PROJECT: {
    icon: ENTITY_ICONS.project,
    overview: "ProjectOverview",
    labelKey: "nav.project.overview",
    routeOf: id => ({ name: "ProjectDetail", params: { projectId: id } }),
  },
  USER: {
    // Employees have no `USER` icon of their own — the drawer, the palette and the detail badge all
    // picture them with the employee one.
    icon: ENTITY_ICONS.employee,
    overview: "EmployeeOverview",
    labelKey: "nav.employee.overview",
    routeOf: id => ({ name: "EmployeeDetail", params: { employeeId: id } }),
  },
  CUSTOMER: {
    icon: ENTITY_ICONS.customer,
    overview: "CustomerOverview",
    labelKey: "nav.customer.overview",
    routeOf: id => ({ name: "CustomerDetail", params: { customerId: id } }),
  },
  MATERIAL: {
    icon: ENTITY_ICONS.material,
    labelKey: "nav.material.overview",
    routeOf: id => ({ name: "MaterialDetail", params: { materialId: id } }),
  },
  MACHINE: {
    icon: ENTITY_ICONS.machine,
    labelKey: "nav.machine.overview",
    routeOf: id => ({ name: "MachineDetail", params: { machineId: id } }),
  },
  ADDITIONAL_EFFORT: {
    icon: ENTITY_ICONS.additionaleffort,
    labelKey: "nav.additionaleffort.overview",
    routeOf: id => ({ name: "AdditionalEffortDetail", params: { additionalEffortId: id } }),
  },
  PACKET: {
    icon: ENTITY_ICONS.packet,
    // A packet bundles records of one catalogue and is listed with it, so the list of packets that
    // point at a record is that record's catalogue, filtered to packets (`services/catalogPackets.js`).
    overview: target => packetListRoute(target.type),
    labelKey: "nav.packet.overview",
    routeOf: id => ({ name: "PacketDetail", params: { packetId: id } }),
  },
  CATEGORY: {
    icon: ENTITY_ICONS.category,
    labelKey: "nav.category.overview",
    routeOf: id => ({ name: "CategoryDetail", params: { categoryId: id } }),
  },
  USER_POSITION: {
    icon: ENTITY_ICONS.userposition,
    labelKey: "nav.userposition.overview",
    routeOf: id => ({ name: "UserPositionDetail", params: { userPositionId: id } }),
  },
  CONSTRUCTION_REPORT: {
    icon: ENTITY_ICONS.constructionreport,
    labelKey: "file.types.constructionreport",
    routeOf: reportRoute(PROJECT_FORM_NAMES.CONSTRUCTION_REPORTS),
  },
  HOURLY_WAGE_REPORT: {
    icon: ENTITY_ICONS.hourlywagereport,
    labelKey: "file.types.hourlywagereport",
    routeOf: reportRoute(PROJECT_FORM_NAMES.HOURLY_WAGE_REPORTS),
  },
  HOURLY_WAGE_INVOICE: {
    icon: "mdi-receipt-text-outline",
    labelKey: "file.types.hourlywageinvoice",
    // A Regierechnung is reached through its project too, on the Stundenlohnberichte tab it was
    // billed from — there is no invoice route.
    routeOf: reportRoute(PROJECT_FORM_NAMES.HOURLY_WAGE_REPORTS),
  },
  CONTACT_POSITION: {
    icon: "mdi-card-account-details-outline",
    labelKey: "file.types.contactposition",
    // Contact roles are edited inside the customer settings, not on a page of their own.
    routeOf: () => null,
  },
}

/**
 * The route for a reference row, or null when that kind of record has nowhere of its own to open and
 * the row did not say which project it belongs to.
 */
export function routeForEntity(type, id, parentId) {
  return ENTITY_TYPES[type]?.routeOf(id, parentId) ?? null
}

/**
 * That kind's own overview, narrowed to the records pointing at `{ type, id }` — where such an
 * overview exists.
 *
 * `overview` is set on four kinds and left off the rest, and that is the territory rather than an
 * oversight: a Bautagesbericht, a Regieschein and a Regierechnung have no cross-project list at all,
 * a Kontaktrolle is edited inside the customer settings, and the remaining kinds never appear as a
 * *referrer* — nothing points at a record by way of a Material. Returns null for all of them, and a
 * caller offers something else instead.
 *
 * `overview` is a route name, or for packets a function of the target returning a route: packets are
 * listed per catalogue, filtered to packets, so which list applies depends on the kind of record they
 * point at.
 */
export function overviewRouteForReferrers(referrerType, target) {
  const overview = ENTITY_TYPES[referrerType]?.overview
  const route = typeof overview === "function" ? overview(target) : overview ? { name: overview } : null
  if (!route) return null
  return {
    ...route,
    query: {
      ...route.query,
      referenceType: target.type,
      referenceId: String(target.id),
      // Display text for the banner on the other end, and nothing else: the two ids above are what
      // the backend filters on. Left off when the record has no name to give, and the banner then
      // says what kind of record it was instead.
      ...(target.name ? { referenceName: target.name } : {}),
    },
  }
}

/**
 * What a project's Protokoll lists besides the project itself, by the backend's `ProjectAuditKind`:
 * the records that belong to the project, grouped the way its tabs group them. They are the Protokoll's
 * filter chips.
 *
 * `permissions` are the ones the project page shows that tab for, so the chips offer what the reader
 * can open next door. The backend applies the finer rule (a report kind on this very project, by
 * assigned/foreign) and leaves out what it refuses, so a chip it disagrees with shows nothing rather
 * than failing. The Regierechnungen have no tab of their own; they are billed from the Stundenlohnberichte
 * tab under a permission of their own.
 */
export const PROJECT_HISTORY_KINDS = [
  {
    value: "PROJECT",
    icon: ENTITY_TYPES.PROJECT.icon,
    labelKey: "general.activity.kind_project",
    permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_GENERAL, PERMISSIONS.VIEW_FOREIGN_PROJECT_GENERAL],
  },
  {
    value: "SERVICE_CATALOG",
    icon: "mdi-format-list-numbered",
    labelKey: "project.detail.tabs.serviceCatalog",
    permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_SERVICE_CATALOG, PERMISSIONS.VIEW_FOREIGN_PROJECT_SERVICE_CATALOG],
  },
  {
    value: "CONSTRUCTION_REPORT",
    icon: ENTITY_TYPES.CONSTRUCTION_REPORT.icon,
    labelKey: ENTITY_TYPES.CONSTRUCTION_REPORT.labelKey,
    permissions: [
      PERMISSIONS.VIEW_ASSIGNED_PROJECT_CONSTRUCTIONREPORT,
      PERMISSIONS.VIEW_FOREIGN_PROJECT_CONSTRUCTIONREPORT,
    ],
  },
  {
    value: "HOURLY_WAGE_REPORT",
    icon: ENTITY_TYPES.HOURLY_WAGE_REPORT.icon,
    labelKey: ENTITY_TYPES.HOURLY_WAGE_REPORT.labelKey,
    permissions: [
      PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT,
      PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT,
    ],
  },
  {
    value: "HOURLY_WAGE_INVOICE",
    icon: ENTITY_TYPES.HOURLY_WAGE_INVOICE.icon,
    labelKey: ENTITY_TYPES.HOURLY_WAGE_INVOICE.labelKey,
    permissions: [
      PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEINVOICE,
      PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEINVOICE,
    ],
  },
]
